import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { KpiCard } from '@/design-system';
import type { PlatformMetrics } from '@/entities/tenant';
import { formatNumber } from '@/shared/lib';

import { paths } from '@/app/router/paths';
import { adminCopy } from '../copy';

export interface MetricsRowProps {
    metrics: PlatformMetrics;
}

/**
 * Vier getallen over alle organisaties heen - niet meer, en de doorlooptijd van
 * het rapport bij de eerste twee: dat is de belofte van het product.
 *
 * Elk getal draagt een vervolgactie (principe 7). Een getal dat een bron nog
 * niet levert is "onbekend", nooit 0.
 */
export function MetricsRow({ metrics }: MetricsRowProps) {
    const count = (value: number | null) =>
        value === null ? adminCopy.metricUnknown : formatNumber(value);
    const leadTime = metrics.averageLeadTimeMinutes;

    return (
        <section
            aria-label={adminCopy.metricsTitle}
            className="mb-[18px] grid grid-cols-[repeat(auto-fit,minmax(182px,1fr))] gap-[14px]"
        >
            <KpiCard
                label={adminCopy.metricOrders}
                value={count(metrics.ordersCreated)}
                delta={<FollowUp to={paths.adminTenants} text={adminCopy.metricsToTenants} />}
            />
            <KpiCard
                label={adminCopy.metricLeadTime}
                value={leadTime === null ? adminCopy.metricUnknown : Math.floor(leadTime / 60)}
                unit={leadTime === null ? undefined : adminCopy.metricLeadTimeUnit(leadTime % 60)}
                tone={leadTime === null ? 'neutral' : 'accent'}
                deltaToneOverride="neutral"
                delta={
                    metrics.days === 0
                        ? adminCopy.metricsEmpty
                        : metrics.complete
                          ? adminCopy.metricReports(metrics.reportsSent ?? 0)
                          : adminCopy.metricsIncomplete
                }
            />
            <KpiCard
                label={adminCopy.metricCredits}
                value={count(metrics.creditsConsumed)}
                delta={<FollowUp to={paths.adminAudit} text={adminCopy.auditTitle} />}
            />
            <KpiCard
                label={adminCopy.metricActiveUsers}
                value={count(metrics.activeUsers)}
                delta={<FollowUp to={paths.adminUsers} text={adminCopy.metricsToUsers} />}
            />
        </section>
    );
}

function FollowUp({ to, text }: { to: string; text: ReactNode }) {
    return (
        <Link
            to={to}
            className="text-[var(--accent)] underline-offset-2 hover:text-[var(--accent-dark)] hover:underline"
        >
            {text}
        </Link>
    );
}
